import { v4 as generateUuid } from 'uuid'

import { shortenShopifyIdToNumeric } from '../../../app/_utils/shortenShopifyId'
import { EMBROIDERY_VERSION_V2 } from '../embroideryVersion'
import {
  V2EmbroideryVariants,
  variantForEmbroideryItem,
} from '../providers/resolveV2EmbroideryVariants'
import { EmbroideryItem, EmbroideryItemOfType } from '../providers/types'
import {
  EmbroideryItemCustomProperties,
  EmbroideryV2GarmentCustomProperties,
  EmbroideryV2ItemCustomProperties,
} from '../types'
import { EmbroideryVariant } from '../useEmbroideryVariants'

import {
  generateV2AttachmentProperties,
  generateV2GarmentProperties,
} from './generateEmbroideryV2LineItemProperties'

export type V2EmbroideryAttachmentLine = {
  item: EmbroideryItem
  variant: EmbroideryVariant
  properties: EmbroideryV2ItemCustomProperties
}

export type V2EmbroideryLineSet = {
  guid: string
  garmentProperties: EmbroideryV2GarmentCustomProperties
  attachmentLines: V2EmbroideryAttachmentLine[]
  setupLine?: { variant: EmbroideryVariant; properties: EmbroideryItemCustomProperties }
}

/** The first logo item: the v2 rules allow at most one, so any others are not set up. */
export const firstLogoItem = (items: readonly EmbroideryItem[]) =>
  items.find((item): item is EmbroideryItemOfType<'logo'> => item.typeKey === 'logo')

const numericVariantId = (variant: EmbroideryVariant | undefined) =>
  shortenShopifyIdToNumeric(variant?.productInfo.shopifyId) ?? undefined

/** The id an item's attachment line carries as `_embroideryId`, or undefined with no variant. */
export const v2EmbroideryVariantId = (item: EmbroideryItem, variants: V2EmbroideryVariants) =>
  numericVariantId(variantForEmbroideryItem(item, variants))

/**
 * Whether a new logo's setup line goes in with this garment: not when an earlier add already put
 * one in the cart.
 */
const addsSetupLine = (items: readonly EmbroideryItem[], logoSetupInCart: boolean) =>
  Boolean(firstLogoItem(items)?.values.requiresSetup) && !logoSetupInCart

/**
 * What the bag has no variant for, or undefined when it can buy every line.
 * `buildV2EmbroideryLineSet` throws when it can't, so check this before offering the shopper an
 * add to bag.
 */
export const missingVariant = (
  items: readonly EmbroideryItem[],
  variants: V2EmbroideryVariants,
  logoSetupInCart: boolean
): string | undefined => {
  const unresolvedItem = items.find(item => v2EmbroideryVariantId(item, variants) === undefined)
  if (unresolvedItem) return `type "${unresolvedItem.typeKey}"`

  const setupResolves = numericVariantId(variants.byPriceKey.logo_setup) !== undefined
  if (addsSetupLine(items, logoSetupInCart) && !setupResolves) return 'logo setup'

  return undefined
}

/**
 * The cart properties for a v2 garment and its attachments, all under one fresh `_guid`.
 * Throws when a line's variant doesn't resolve, rather than adding the garment without it.
 */
export function buildV2EmbroideryLineSet({
  items,
  variants,
  productColorHex,
  logoSetupInCart,
}: {
  items: readonly EmbroideryItem[]
  variants: V2EmbroideryVariants
  productColorHex: string | undefined
  logoSetupInCart: boolean
}): V2EmbroideryLineSet {
  const missing = missingVariant(items, variants, logoSetupInCart)
  if (missing !== undefined) throw new Error(`No v2 embroidery variant resolved for ${missing}`)

  const guid = generateUuid()
  const logoItem = firstLogoItem(items)
  const setupVariant = variants.byPriceKey.logo_setup
  const setupLineVariant = addsSetupLine(items, logoSetupInCart) ? setupVariant : undefined

  const attachmentLines = items.map((item): V2EmbroideryAttachmentLine => {
    // Both resolve: `missingVariant` checked every item.
    const variant = variantForEmbroideryItem(item, variants)!
    const properties = generateV2AttachmentProperties(item, {
      guid,
      productColorHex,
      embroideryVariantId: numericVariantId(variant)!,
      logoSetupVariantId: item === logoItem ? numericVariantId(setupVariant) : undefined,
    })

    return {
      item,
      variant,
      properties:
        item === logoItem && setupLineVariant
          ? { ...properties, _logo_setup_fee: setupLineVariant.productInfo.price.amount.toString() }
          : properties,
    }
  })

  const logoLine = attachmentLines.find(line => line.item === logoItem)

  return {
    guid,
    garmentProperties: generateV2GarmentProperties({ guid, logoId: logoItem?.values.logoFile?.id }),
    attachmentLines,
    setupLine:
      logoLine && setupLineVariant
        ? { variant: setupLineVariant, properties: setupLineProperties(logoLine.properties) }
        : undefined,
  }
}

// Legacy-shaped setup line (see useMakeEmbroideredCartItem), with its own `_guid`.
function setupLineProperties(
  logoProperties: EmbroideryV2ItemCustomProperties
): EmbroideryItemCustomProperties {
  return {
    _embroidery_version: EMBROIDERY_VERSION_V2,
    _scrub_color: logoProperties._scrub_color,
    _logo_id: logoProperties._logo_id,
    _guid: logoProperties._logo_setup_guid,
    _logo_preview: logoProperties._logo_preview,
    logo_name: logoProperties.logo_name,
    logo_placement: logoProperties.logo_placement,
    logo_color_option: logoProperties.logo_color_option,
    _is_logo_setup: 'true',
  }
}
